<template>
  <b-modal
    id="add-new-price-list-modal"
    :visible="priceListAddSidebarActive"
    title="Nueva Lista de Precios"
    size="xl"
    scrollable
    no-close-on-backdrop
    @change="(visible) => $emit('update:price-list-add-sidebar-active', visible)"
  >
    <validation-observer ref="simpleRules">
      <b-form @submit.prevent="onSubmit">
        <b-row>
          <b-col
            cols="12"
            md="5"
          >
            <validation-provider
              v-slot="{ errors }"
              name="nombre"
              rules="required"
            >
              <b-form-group
                label="Nombre:"
                label-for="input-name"
              >
                <b-form-input
                  id="input-name"
                  v-model="form.name"
                  placeholder="Ingresa el nombre de la lista de precios"
                  :state="!errors[0]"
                />
                <b-form-invalid-feedback>
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </b-col>

          <b-col
            cols="12"
            md="7"
          >
            <b-form-group
              label="Descripción:"
              label-for="input-description"
            >
              <b-form-input
                id="input-description"
                v-model="form.description"
                placeholder="Para qué se usa esta lista"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!--
          La lista nace con el universo completo al precio base. Crear una lista
          exige apartarse del base en al menos una variante: si no, no aporta nada.
        -->
        <b-alert
          :variant="customizedCount > 0 ? 'success' : 'warning'"
          show
          class="mb-1"
        >
          <div class="alert-body d-flex align-items-center">
            <feather-icon
              :icon="customizedCount > 0 ? 'CheckCircleIcon' : 'AlertCircleIcon'"
              size="18"
              class="mr-1"
            />
            <span v-if="customizedCount > 0">
              {{ customizedCount }} de {{ universe.length }} variantes con precio propio.
              El resto nace con el precio base de su estilo y lo seguirá automáticamente.
            </span>
            <span v-else>
              Modifica al menos un precio para poder crear la lista.
              Las variantes que dejes sin tocar seguirán el precio base de su estilo.
            </span>
          </div>
        </b-alert>

        <b-row class="mb-1">
          <b-col
            cols="12"
            md="6"
          >
            <b-form-input
              v-model="searchQuery"
              placeholder="Buscar por SKU, código o descripción..."
            />
          </b-col>

          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-md-end mt-1 mt-md-0"
          >
            <b-form-checkbox
              v-model="onlyCustomized"
              switch
            >
              Ver solo las modificadas
            </b-form-checkbox>
          </b-col>
        </b-row>

        <div class="price-universe">
          <b-table
            :items="visibleRows"
            :fields="universeFields"
            :busy="isLoadingUniverse"
            small
            hover
            responsive
            show-empty
            empty-text="No hay variantes que coincidan con la búsqueda"
            primary-key="product_variant_id"
          >
            <template #table-busy>
              <div class="text-center text-dark my-2">
                <b-spinner class="align-middle" />
              </div>
            </template>

            <template #cell(sku)="data">
              <div class="font-weight-bold text-primary">
                {{ data.item.sku }}
              </div>
              <small class="text-muted">{{ data.item.description }}</small>
            </template>

            <template #cell(base_price)="data">
              <span class="text-muted">Q{{ Number(data.item.base_price).toFixed(2) }}</span>
            </template>

            <template #cell(fixed_price)="data">
              <b-form-input
                v-model.number="prices[data.item.product_variant_id]"
                type="number"
                min="0"
                step="0.01"
                size="sm"
                class="price-universe__input"
              />
            </template>

            <template #cell(state)="data">
              <b-badge
                v-if="isCustomized(data.item)"
                variant="light-warning"
              >
                Editado
              </b-badge>
              <b-badge
                v-else
                variant="light-secondary"
              >
                Precio base
              </b-badge>
            </template>
          </b-table>
        </div>
      </b-form>
    </validation-observer>

    <template #modal-footer>
      <div class="d-flex justify-content-end w-100">
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          class="mr-1"
          :disabled="isSubmitting || customizedCount === 0"
          @click="onSubmit"
        >
          <b-spinner
            v-if="isSubmitting"
            small
            class="mr-1"
          />
          Crear lista
        </b-button>

        <b-button
          v-ripple.400="'rgba(186, 191, 199, 0.15)'"
          variant="outline-secondary"
          @click="$emit('update:price-list-add-sidebar-active', false)"
        >
          Cancelar
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import axios from '@axios'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, decimal } from '@/utils/validations/validations'
import Ripple from 'vue-ripple-directive'

/** Tolerancia de comparación, igual que la del backend. */
const EPSILON = 0.01

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
  },

  directives: {
    Ripple,
  },

  props: {
    priceListAddSidebarActive: {
      type: Boolean,
      default: false,
      required: true,
    },
  },

  data() {
    return {
      required,
      decimal,
      isSubmitting: false,
      isLoadingUniverse: false,

      form: {
        name: '',
        description: '',
        active: true,
      },

      // Universo de variantes con su precio base, tal como lo entrega el backend.
      universe: [],
      // Precio editable por variante, indexado por product_variant_id.
      prices: {},

      searchQuery: '',
      onlyCustomized: false,

      universeFields: [
        { key: 'sku', label: 'Variante' },
        { key: 'category', label: 'Categoría' },
        { key: 'size', label: 'Talla' },
        { key: 'base_price', label: 'Precio Base' },
        { key: 'fixed_price', label: 'Precio en la Lista' },
        { key: 'state', label: 'Estado' },
      ],
    }
  },

  computed: {
    /** Cuántas variantes se apartaron del precio base. */
    customizedCount() {
      return this.universe.filter(row => this.isCustomized(row)).length
    },

    visibleRows() {
      const term = this.searchQuery.trim().toLowerCase()

      return this.universe.filter(row => {
        if (this.onlyCustomized && !this.isCustomized(row)) return false
        if (!term) return true

        return `${row.sku} ${row.code} ${row.description}`.toLowerCase().includes(term)
      })
    },
  },

  watch: {
    priceListAddSidebarActive(newVal) {
      if (newVal) {
        this.resetForm()
        this.loadUniverse()
      }
    },
  },

  methods: {
    isCustomized(row) {
      const price = Number(this.prices[row.product_variant_id])
      if (Number.isNaN(price)) return false

      return Math.abs(price - Number(row.base_price)) >= EPSILON
    },

    async loadUniverse() {
      try {
        this.isLoadingUniverse = true

        const response = await axios.get('price-list-universe')
        this.universe = response.data.data

        const prices = {}
        this.universe.forEach(row => {
          prices[row.product_variant_id] = Number(row.base_price)
        })
        this.prices = prices
      } catch (error) {
        this.showErrors(error)
      } finally {
        this.isLoadingUniverse = false
      }
    },

    resetForm() {
      this.form = { name: '', description: '', active: true }
      this.searchQuery = ''
      this.onlyCustomized = false
      this.universe = []
      this.prices = {}

      if (this.$refs.simpleRules) this.$refs.simpleRules.reset()
    },

    async onSubmit() {
      try {
        const isValid = await this.$refs.simpleRules.validate()
        if (!isValid) return

        if (this.customizedCount === 0) {
          this.makeToast(
            'warning',
            'Falta modificar precios',
            'Debes modificar al menos un precio respecto del precio base.',
          )
          return
        }

        this.isSubmitting = true

        // Solo viajan las variantes editadas: el resto las puebla el backend
        // con el precio base.
        const response = await axios.post('price-list', {
          ...this.form,
          prices: this.universe
            .filter(row => this.isCustomized(row))
            .map(row => ({
              product_variant_id: row.product_variant_id,
              fixed_price: Number(this.prices[row.product_variant_id]),
            })),
        })

        this.makeToast('success', 'Lista creada', 'La lista de precios se ha creado correctamente.')

        this.$emit('refreshData')
        this.$emit('update:price-list-add-sidebar-active', false)

        if (response.data.data) {
          this.$emit('addPriceListTab', response.data.data.id)
        }
      } catch (error) {
        this.showErrors(error)
      } finally {
        this.isSubmitting = false
      }
    },

    showErrors(error) {
      if (error.response && error.response.data && error.response.data.data) {
        const errors = error.response.data.data
        Object.keys(errors).forEach(field => {
          this.makeToast('danger', 'Error', `${field}: ${errors[field][0]}`)
        })
      } else if (error.response && error.response.data && error.response.data.error) {
        this.makeToast('danger', 'Error', error.response.data.error)
      } else {
        this.makeToast('danger', 'Error', 'Ha ocurrido un error inesperado.')
      }
    },

    makeToast(variant = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.price-universe {
  max-height: 45vh;
  overflow-y: auto;

  &__input {
    max-width: 120px;
  }
}
</style>
